import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getAuthToken } from "../api/axiosClient";
import { getTenant } from "../components/common/utils/common";

function parseSSEEvents(rawText) {
  const events = [];
  const rawEvents = rawText.split(/\r?\n\r?\n/);
  const remaining = rawEvents.pop() ?? "";

  for (const rawEvent of rawEvents) {
    if (!rawEvent.trim()) continue;

    const lines = rawEvent.split(/\r?\n/);
    let eventType = "message";
    const dataLines = [];
    let id = null;
    let retry = null;

    for (const line of lines) {
      if (line.startsWith(":")) {
        // SSE comment / heartbeat ping
        continue;
      }
      if (line.startsWith("event:")) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trimStart());
      } else if (line.startsWith("id:")) {
        id = line.slice(3).trim();
      } else if (line.startsWith("retry:")) {
        const parsedRetry = parseInt(line.slice(6).trim(), 10);
        if (!isNaN(parsedRetry)) retry = parsedRetry;
      }
    }

    if (dataLines.length > 0 || eventType !== "message") {
      events.push({
        event: eventType,
        data: dataLines.join("\n"),
        id,
        retry,
      });
    }
  }

  return { events, remaining };
}

/**
 * Generic hook for streaming SSE endpoints.
 * Automatically retrieves the access token and tenant ID without passing them as parameters.
 * Uses native fetch with ReadableStream to avoid browser XHR/Axios buffering and timeout issues.
 *
 * @param {string} url - SSE endpoint (e.g. "/notifications/stream")
 * @param {Function} onMessage - Callback invoked when a message arrives: (data, rawEvent) => void
 * @param {Object} [options={}] - Optional configuration
 * @param {boolean} [options.enabled=true] - Toggle streaming on/off
 * @param {boolean} [options.autoReconnect=true] - Reconnect automatically on drop
 * @param {number} [options.retryInterval=3000] - Base delay for reconnection in ms
 * @param {number} [options.maxRetries=5] - Maximum retry attempts before stopping
 * @param {boolean} [options.parseJson=true] - Automatically run JSON.parse on event data
 * @param {Function} [options.onError] - Error callback: (err) => void
 * @param {Function} [options.onOpen] - Connection opened callback: (response) => void
 * @param {Function} [options.onClose] - Connection closed callback: () => void
 */
export function useEventStream(url, onMessage, options = {}) {
  const {
    enabled = true,
    autoReconnect = true,
    retryInterval = 3000,
    maxRetries = 5,
    parseJson = true,
    onError,
    onOpen,
    onClose,
  } = options;

  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [status, setStatus] = useState("idle"); // 'idle' | 'connecting' | 'connected' | 'error'
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);

  const callbacksRef = useRef({ onMessage, onError, onOpen, onClose });
  callbacksRef.current = { onMessage, onError, onOpen, onClose };

  const lastEventIdRef = useRef(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isManuallyClosedRef = useRef(false);

  const close = useCallback(() => {
    isManuallyClosedRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus("idle");
    callbacksRef.current.onClose?.();
  }, []);

  const connect = useCallback(async () => {
    if (!url || !enabled || !isAuthenticated) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    isManuallyClosedRef.current = false;

    setStatus("connecting");
    setError(null);

    let token = null;
    try {
      token =
        (await getAuthToken?.()) ||
        (await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUDIENCE,
            scope: "openid profile email offline_access",
          },
        }));
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error("Failed to retrieve token for stream:", err);
        setError(err);
        setStatus("error");
      }
      return;
    }

    if (!token || controller.signal.aborted) return;

    const hostname = process.env.REACT_APP_API_HOST_NAME || "";
    const baseURL = `${hostname}/api`;
    const fullUrl = url.startsWith("http")
      ? url
      : `${baseURL}${url.startsWith("/") ? "" : "/"}${url}`;

    async function stream() {
      let serverRetryDelay = retryInterval;

      try {
        const headers = {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
          "X-TenantId": getTenant(),
          ...(lastEventIdRef.current
            ? { "Last-Event-ID": lastEventIdRef.current }
            : {}),
        };

        const response = await fetch(fullUrl, {
          method: "GET",
          headers,
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `SSE stream error: HTTP ${response.status} ${response.statusText}`,
          );
        }

        if (!response.body) {
          throw new Error(
            "ReadableStream not supported by browser environment",
          );
        }

        setStatus("connected");
        retryCountRef.current = 0;
        callbacksRef.current.onOpen?.(response);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const { events, remaining } = parseSSEEvents(buffer);
          buffer = remaining;

          for (const ev of events) {
            if (ev.id) lastEventIdRef.current = ev.id;
            if (ev.retry) serverRetryDelay = ev.retry;

            let parsedPayload = ev.data;
            if (parseJson && ev.data) {
              try {
                parsedPayload = JSON.parse(ev.data);
              } catch {
                parsedPayload = ev.data;
              }
            }

            setLastMessage(parsedPayload);
            callbacksRef.current.onMessage?.(parsedPayload, ev);
          }
        }

        setStatus("idle");
        callbacksRef.current.onClose?.();
      } catch (err) {
        if (err?.name === "AbortError" || controller.signal.aborted) {
          return;
        }

        setError(err);
        setStatus("error");
        callbacksRef.current.onError?.(err);

        if (
          autoReconnect &&
          !isManuallyClosedRef.current &&
          retryCountRef.current < maxRetries
        ) {
          retryCountRef.current += 1;
          const delay = serverRetryDelay * Math.min(retryCountRef.current, 5);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      }
    }

    stream();
  }, [
    url,
    enabled,
    isAuthenticated,
    getAccessTokenSilently,
    retryInterval,
    maxRetries,
    autoReconnect,
    parseJson,
  ]);

  const reconnect = useCallback(() => {
    retryCountRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    if (enabled && url && isAuthenticated) {
      connect();
    } else {
      close();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, url, isAuthenticated, connect, close]);

  return {
    status,
    isConnected: status === "connected",
    isConnecting: status === "connecting",
    lastMessage,
    error,
    reconnect,
    close,
  };
}

export default useEventStream;
